import React, { useCallback, useMemo, useRef, useState } from 'react';
import { GetStaticProps, NextPage } from 'next';
import styled from 'styled-components';
import { Affix, Anchor, Col, Row } from 'antd';

import {
  OFFICES_URL_PREFIX,
  SEARCH_BY_COUNTY_SUFFIX,
  SITE_URL,
} from '../../common/consts';
import Office from '../../backend/s3/office';
import Flat from '../../backend/s3/flat';
import {
  BuyHouseOption,
  computeBuyHouseOptions,
} from '../../common/helpers/buyHouseOption.utils';
import {
  computeOfficesByCountyArray,
  computeOfficeSearchOptions,
} from '../../common/helpers/office.utils';
import { slugifySearchQuery } from '../../common/helpers/searchQuery.utils';
import { generateOfficeListPageStructuredData } from '../../common/helpers/structuredData.utils';
import useCountyCityZone from '../../common/hooks/useCountyCityZone';
import useI18n from '../../common/hooks/useI18n';
import { IOffice } from '../../common/model/office.model';

import {
  Footer,
  Head,
  Header,
  OfficesRowContainer,
  TextSection,
} from '../../components/shared';
import {
  LocationTitle,
  OfficesBreadcrumb,
  OfficesMap,
  OfficesSearchHero,
  TabSelector,
} from '../../components/offices';

type StaticProps = {
  offices: IOffice[];
  buyHouseOptions: BuyHouseOption[];
};

type Props = StaticProps;

const Layout = styled.div`
  display: flex;
  flex: auto;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 0;

  font-style: ${(props) => props.theme.font.style};
`;

const Content = styled.main`
  position: relative;
  flex: auto;
  margin-top: ${(props) => props.theme.headerHeight};

  @media ${(props) => props.theme.breakpoints.mdd} {
    margin-top: 0;
  }
`;

const HeroBottomDivider = styled.div`
  padding-top: 3rem;

  @media ${(props) => props.theme.breakpoints.smd} {
    padding-top: 2rem;
  }
`;

const BreadcrumbContainer = styled.div`
  margin-bottom: -1rem;
`;

const ContentWithPadding = styled.div`
  padding-left: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  padding-right: ${(props) => props.theme.grid.getGridColumns(2, 1)};
`;

const ContentWithMarginTop = styled.div`
  position: relative;
  margin-top: 3rem;

  @media ${(props) => props.theme.breakpoints.smd} {
    margin-top: 2rem;
  }
`;

const ContentWithNegativeMarginTop = styled.div`
  position: relative;
  margin-top: -3rem;

  @media ${(props) => props.theme.breakpoints.smd} {
    margin-top: -2rem;
  }
`;

const InvisibleAnchor = styled(Anchor)`
  display: none;
`;

const StyledAffix = styled(Affix)`
  margin-top: 1.5rem;
  margin-bottom: 3rem;

  @media ${(props) => props.theme.breakpoints.smd} {
    margin-top: 1rem;
    margin-bottom: -1rem;
  }
`;

const OfficesPage: NextPage<Props> = ({ offices, buyHouseOptions }) => {
  const i18n = useI18n();
  const [county, city, _] = useCountyCityZone();

  const [autoCompleteValue, setAutoCompleteValue] = useState('');
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const tabSelectorRef = useRef<HTMLDivElement>(null);
  const scrollingProgramatically = useRef(false);

  const structuredData = useMemo(
    () => generateOfficeListPageStructuredData(offices, i18n, [county, city]),
    [city, county, i18n, offices]
  );

  const officesByCountyArray = useMemo(
    () => computeOfficesByCountyArray(offices),
    [offices]
  );
  const tabs = useMemo(() => officesByCountyArray.map((o) => o[0]), [
    officesByCountyArray,
  ]);
  const options = useMemo(() => computeOfficeSearchOptions(offices), [offices]);

  const onAnchorChange = useCallback((link: string) => {
    if (!scrollingProgramatically.current) {
      const index = parseInt(link.substring(1), 10);
      setSelectedTabIndex(index);
    }
  }, []);

  const setSelectedTabIndexByClick = useCallback(
    (newSelectedTabIndex: number) => {
      scrollingProgramatically.current = true;

      if (newSelectedTabIndex !== selectedTabIndex) {
        setSelectedTabIndex(newSelectedTabIndex);
        window.location.hash = `#${newSelectedTabIndex}`;
      }

      setTimeout(() => {
        scrollingProgramatically.current = false;
        setSelectedTabIndex(newSelectedTabIndex);
      }, 500);
    },
    [selectedTabIndex]
  );

  const getQuery = useCallback(
    (county) => `${slugifySearchQuery(county)}${SEARCH_BY_COUNTY_SUFFIX}`,
    []
  );

  return (
    <Layout>
      <Head
        title={i18n.t('dynamic.officesByCountyPage.q.metaTitle')}
        description={i18n.t('dynamic.officesByCountyPage.q.metaDescription')}
        canonicalUrl={`${SITE_URL}/${OFFICES_URL_PREFIX}`}
      >
        {structuredData && (
          <script
            id="structured-data-script"
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
          />
        )}
      </Head>

      <Header offices={offices} buyHouseOptions={buyHouseOptions} />

      <Content>
        <OfficesSearchHero
          autoCompleteValue={autoCompleteValue}
          autoCompleteOptions={options.filter((o) =>
            o.text
              .toLowerCase()
              .includes(autoCompleteValue.toLowerCase().trim())
          )}
          groupedByCity={false}
          onAutoCompleteValueChange={setAutoCompleteValue}
          imageUrl={i18n.stagingT('dynamic.officesByCountyPage.q.heroImageUrl')}
          i18nKeyPrefix={'dynamic.officesByCountyPage.q.'}
        />
        <HeroBottomDivider />

        <ContentWithPadding>
          <BreadcrumbContainer>
            <OfficesBreadcrumb offices={offices} />
          </BreadcrumbContainer>

          <ContentWithMarginTop>
            <TextSection
              title={i18n.t('dynamic.officesByCountyPage.q.descriptionTitle')}
              content={i18n.t('dynamic.officesByCountyPage.q.descriptionText')}
            />
          </ContentWithMarginTop>

          <ContentWithMarginTop>
            <Row gutter={[32, 32]} justify="center">
              <Col xs={24} xxl={16}>
                {typeof google !== 'undefined' &&
                  typeof google.maps.Map !== 'undefined' && (
                    <OfficesMap offices={offices} />
                  )}
              </Col>
            </Row>
          </ContentWithMarginTop>
        </ContentWithPadding>

        <InvisibleAnchor affix={false} onChange={onAnchorChange}>
          {officesByCountyArray.map((officesByCounty, index) => (
            <Anchor.Link
              key={`${officesByCounty[0]}-anchor`}
              href={`#${index}`}
              title={officesByCounty[0]}
            />
          ))}
        </InvisibleAnchor>

        <StyledAffix>
          <TabSelector
            tabs={tabs}
            allowSliding
            selectedTabIndex={selectedTabIndex}
            ref={tabSelectorRef}
            setSelectedTabIndex={setSelectedTabIndexByClick}
          />
        </StyledAffix>

        <ContentWithNegativeMarginTop />
        {officesByCountyArray.map((officesByCounty, index) => (
          <ContentWithMarginTop key={officesByCounty[0]} as="div">
            <div
              id={index + ''}
              style={{
                position: 'absolute',
                top: `calc(-1.1 * ${
                  tabSelectorRef.current
                    ? tabSelectorRef.current.offsetHeight
                    : 80
                }px)`,
              }}
            />
            <ContentWithPadding>
              <LocationTitle
                query={getQuery(officesByCounty[0])}
                location={officesByCounty[0]}
              />
            </ContentWithPadding>
            <OfficesRowContainer offices={officesByCounty[1]} />
          </ContentWithMarginTop>
        ))}

        <Footer />
      </Content>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps<StaticProps> = async () => {
  const offices = await Office.getOffices();
  const [_, searchOptions] = await Flat.getFlats();

  const buyHouseOptions = computeBuyHouseOptions(searchOptions);

  return {
    props: {
      offices,
      buyHouseOptions,
    },
  };
};

export default OfficesPage;
